/**
 * Mobile drawer TD mega menu — swaps menu body while header + search stay fixed.
 * Uses the same td_mega_menu block data as desktop.
 */
(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 999px)');
  const MOTION_MQ = window.matchMedia('(prefers-reduced-motion: no-preference)');

  const DURATION_OUT = 200;
  const DURATION_IN = 250;

  // Generation token per sidebar: each open/close call bumps it, and any
  // in-flight call whose token becomes stale stops mutating the DOM. This
  // prevents a slow "open" animation from finishing (and re-showing the
  // mega panel) after the drawer has already been closed.
  const tokens = new WeakMap();

  function prefersMotion() {
    return MOTION_MQ.matches;
  }

  function nextToken(sidebar) {
    const token = (tokens.get(sidebar) || 0) + 1;
    tokens.set(sidebar, token);
    return token;
  }

  function isCurrent(sidebar, token) {
    return tokens.get(sidebar) === token;
  }

  function cancelAnimations(element) {
    if (!element || typeof element.getAnimations !== 'function') {
      return;
    }

    element.getAnimations().forEach((animation) => animation.cancel());
  }

  function clearAnimationStyles(element) {
    if (!element) {
      return;
    }

    cancelAnimations(element);
    element.style.opacity = '';
    element.style.transform = '';
  }

  function animateView(element, keyframes, duration) {
    if (!element) {
      return Promise.resolve();
    }

    return element
      .animate(keyframes, {
        duration,
        easing: 'ease-out',
        fill: 'both',
      })
      .finished.catch(() => {});
  }

  async function fadeOutView(element, duration = DURATION_OUT) {
    if (!element) {
      return;
    }

    if (!prefersMotion()) {
      element.hidden = true;
      return;
    }

    await animateView(
      element,
      [
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: 'translateY(-8px)' },
      ],
      duration
    );

    element.hidden = true;
    clearAnimationStyles(element);
  }

  async function fadeInView(element, duration = DURATION_IN) {
    if (!element) {
      return;
    }

    element.hidden = false;

    if (!prefersMotion()) {
      return;
    }

    await animateView(
      element,
      [
        { opacity: 0, transform: 'translateY(8px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      duration
    );

    clearAnimationStyles(element);
  }

  function resetMegaViews(sidebar) {
    sidebar.querySelectorAll('.header-sidebar__mega-view').forEach((view) => {
      cancelAnimations(view);
      view.hidden = true;
      clearAnimationStyles(view);
    });
  }

  function resetOpenButtons(sidebar) {
    sidebar.querySelectorAll('[data-action="open-td-mega"]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
  }

  /**
   * Unconditional, synchronous reset used when the drawer itself closes.
   * Always wins over any open/close animation still in flight, so the mega
   * menu can never get stuck hiding the root view on the next open.
   */
  function forceCloseTdMega(sidebar) {
    nextToken(sidebar);

    const rootView = sidebar.querySelector('.header-sidebar__root-view');
    const cta = sidebar.querySelector('.header-sidebar__cta');

    sidebar.classList.remove('is-td-mega-open');
    resetMegaViews(sidebar);
    resetOpenButtons(sidebar);

    if (rootView) {
      cancelAnimations(rootView);
      rootView.hidden = false;
      clearAnimationStyles(rootView);
    }

    if (cta) {
      cancelAnimations(cta);
      cta.hidden = false;
      clearAnimationStyles(cta);
    }
  }

  async function closeTdMega(sidebar) {
    const token = nextToken(sidebar);

    const rootView = sidebar.querySelector('.header-sidebar__root-view');
    const activePanel = sidebar.querySelector('.header-sidebar__mega-view:not([hidden])');
    const cta = sidebar.querySelector('.header-sidebar__cta');

    if (!sidebar.classList.contains('is-td-mega-open') && !activePanel) {
      resetMegaViews(sidebar);
      resetOpenButtons(sidebar);
      if (rootView) {
        rootView.hidden = false;
      }
      return;
    }

    if (prefersMotion()) {
      await fadeOutView(activePanel);
      if (!isCurrent(sidebar, token)) {
        return;
      }
    } else {
      resetMegaViews(sidebar);
    }

    sidebar.classList.remove('is-td-mega-open');
    resetMegaViews(sidebar);
    resetOpenButtons(sidebar);

    if (prefersMotion()) {
      await fadeInView(rootView);
      if (!isCurrent(sidebar, token)) {
        return;
      }

      if (cta) {
        await fadeInView(cta, 200);
        if (!isCurrent(sidebar, token)) {
          return;
        }
      }
    } else if (rootView) {
      rootView.hidden = false;
      if (cta) {
        cta.hidden = false;
      }
    }
  }

  async function openTdMega(sidebar, button) {
    const panelId = button.getAttribute('data-td-mega-panel');
    const panel = panelId ? sidebar.querySelector(`#${panelId}`) : null;

    if (!panel) {
      return;
    }

    const token = nextToken(sidebar);
    const rootView = sidebar.querySelector('.header-sidebar__root-view');
    const cta = sidebar.querySelector('.header-sidebar__cta');

    sidebar.querySelectorAll('[data-action="open-td-mega"]').forEach((item) => {
      item.setAttribute('aria-expanded', item === button ? 'true' : 'false');
    });

    if (prefersMotion()) {
      await Promise.all([fadeOutView(rootView), cta ? fadeOutView(cta, 150) : Promise.resolve()]);
      if (!isCurrent(sidebar, token)) {
        return;
      }
    } else if (rootView) {
      rootView.hidden = true;
      if (cta) {
        cta.hidden = true;
      }
    }

    sidebar.classList.add('is-td-mega-open');
    resetMegaViews(sidebar);
    panel.hidden = false;

    if (prefersMotion()) {
      await fadeInView(panel);
      if (!isCurrent(sidebar, token)) {
        return;
      }
    }
  }

  function initSidebar(sidebar) {
    if (sidebar.dataset.tdMegaSidebarInit === 'true') {
      return;
    }

    sidebar.dataset.tdMegaSidebarInit = 'true';

    sidebar.addEventListener(
      'click',
      (event) => {
        if (!MOBILE_MQ.matches) {
          return;
        }

        const openButton = event.target.closest('[data-action="open-td-mega"]');
        if (openButton) {
          event.preventDefault();
          event.stopImmediatePropagation();
          void openTdMega(sidebar, openButton);
          return;
        }

        const closeButton = event.target.closest('[data-action="close-td-mega"]');
        if (closeButton) {
          event.preventDefault();
          void closeTdMega(sidebar);
        }
      },
      true
    );

    sidebar.addEventListener('dialog:after-hide', () => {
      forceCloseTdMega(sidebar);
    });
  }

  function init() {
    document.querySelectorAll('header-sidebar').forEach(initSidebar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
